/**
 * Liste catalogue · formulaire (clic ligne = édition, pas de fiche lecture).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { facturxFetch } from "../api/client";
import { useHubBack } from "../mobile/hubBack";
import { Button } from "../components/Button";
import { CheckField } from "../components/CheckField";
import { ConfirmModal } from "../components/ConfirmModal";
import { PageHeader } from "../components/PageHeader";
import { CatalogForm } from "../features/catalog/CatalogForm";
import { CatalogListBar } from "../features/catalog/CatalogListBar";
import { CatalogTable } from "../features/catalog/CatalogTable";
import { isActive, productMatches } from "../features/catalog/format";
import {
  asProduct,
  emptyDraft,
  inputToCents,
  productToCopyDraft,
  productToDraft,
  type Product,
  type ProductDraft,
  type TaxRate,
} from "../features/catalog/types";
import { formatsFromCompany } from "../formats/types";
import { useCompany } from "../company/CompanyProvider";

type CatalogPageProps = {
  companyId: number;
  seedId?: number | null;
  onSeedUsed?: () => void;
  onCreateQuote?: (productId: number) => void;
  onCreateInvoice?: (productId: number) => void;
};
type View = "list" | "form";

type TaxesPayload = {
  default_tax_rate_id: number | null;
  tax_rates: TaxRate[];
};

export function CatalogPage({ companyId, seedId, onSeedUsed, onCreateQuote, onCreateInvoice }: CatalogPageProps) {
  const { company } = useCompany();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const [rows, setRows] = useState<Product[]>([]);
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [defaultTaxId, setDefaultTaxId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [view, setView] = useState<View>("list");
  const [draft, setDraft] = useState<ProductDraft | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "off" | "purge"; id: number; label: string } | null>(null);
  const [priceBase, setPriceBase] = useState<number | null>(null);
  const [priceAsk, setPriceAsk] = useState<{
    body: Record<string, unknown>;
    drafts: number;
    recurring: number;
    applyDrafts: boolean;
    applyRecurring: boolean;
  } | null>(null);
  useHubBack((view === "form" ? 1 : 0) + (confirm || priceAsk ? 1 : 0), () => {
    if (priceAsk) {
      setPriceAsk(null);
      return;
    }
    if (confirm) {
      setConfirm(null);
      return;
    }
    setError("");
    setDraft(null);
    setPriceBase(null);
    setView("list");
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams({ company_id: String(companyId) });
    if (showInactive) params.set("all", "1");
    return facturxFetch<{ products: Product[] }>(`products.php?${params}`);
  }, [companyId, showInactive]);

  const visible = useMemo(() => rows.filter((p) => productMatches(p, q)), [rows, q]);

  async function refresh() {
    const data = await load();
    const next = (data.products || []).map(asProduct);
    setRows(next);
    return next;
  }

  useEffect(() => {
    setView("list");
    setDraft(null);
    setQ("");
    setMenuId(null);
    setConfirm(null);
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    facturxFetch<TaxesPayload>(`tax-rates.php?company_id=${companyId}`)
      .then((data) => {
        if (stop) return;
        setRates(
          (data.tax_rates || []).map((r) => ({
            ...r,
            id: Number(r.id),
            rate_bp: Number(r.rate_bp),
          }))
        );
        setDefaultTaxId(data.default_tax_rate_id === null ? null : Number(data.default_tax_rate_id));
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Taux indisponibles");
      });
    return () => {
      stop = true;
    };
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    setError("");
    load()
      .then((data) => {
        if (!stop) setRows((data.products || []).map(asProduct));
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      });
    return () => {
      stop = true;
    };
  }, [load]);

  async function openCopy(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ product: Product }>(`products.php?id=${id}`);
      setPriceBase(null);
      setDraft(productToCopyDraft(asProduct(data.product), decimalComma));
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!seedId) return;
    void openEdit(seedId).finally(() => onSeedUsed?.());
  }, [seedId]);

  async function openEdit(id: number) {
    setBusy(true);
    setError("");
    try {
      const data = await facturxFetch<{ product: Product }>(`products.php?id=${id}`);
      const product = asProduct(data.product);
      setPriceBase(Number(product.unit_price_ht_cents) || 0);
      setDraft(productToDraft(product, decimalComma));
      setView("form");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function goList() {
    setError("");
    setDraft(null);
    setPriceBase(null);
    setPriceAsk(null);
    setView("list");
  }

  async function commitSave(body: Record<string, unknown>, applyDrafts: boolean, applyRecurring: boolean) {
    if (!draft) return;
    setBusy(true);
    setError("");
    try {
      const payload = { ...body, apply_drafts: applyDrafts ? 1 : 0, apply_recurring: applyRecurring ? 1 : 0 };
      if (draft.id) {
        await facturxFetch<{ product: Product }>(`products.php?id=${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await facturxFetch<{ product: Product }>("products.php", {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      setPriceAsk(null);
      setDraft(null);
      setPriceBase(null);
      await refresh();
      setView("list");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    const cents = inputToCents(draft.price, decimalComma);
    if (cents === null || cents < 0) {
      setError("Prix invalide.");
      return;
    }
    const costCents = inputToCents(draft.cost, decimalComma);
    if (costCents === null || costCents < 0) {
      setError("Coût invalide.");
      return;
    }
    if (!draft.label.trim()) {
      setError("Le libellé est obligatoire.");
      return;
    }
    const body = {
      company_id: companyId,
      label: draft.label.trim(),
      description: draft.description.trim(),
      product_code: draft.product_code.trim(),
      unit: draft.unit.trim() || "u",
      unit_price_ht_cents: cents,
      cost_ht_cents: costCents,
      default_qty: draft.default_qty,
      pcg_code: draft.pcg_code,
      pcg_sub: draft.pcg_sub,
      tax_rate_id: draft.tax_rate_id,
      op_kind: draft.op_kind,
    };
    if (draft.id && priceBase !== null && cents !== priceBase) {
      setBusy(true);
      setError("");
      try {
        const usage = await facturxFetch<{ drafts: number; recurring: number }>(`products.php?id=${draft.id}&usage=1`);
        const drafts = Number(usage.drafts) || 0;
        const recurring = Number(usage.recurring) || 0;
        if (drafts > 0 || recurring > 0) {
          setPriceAsk({
            body,
            drafts,
            recurring,
            applyDrafts: drafts > 0,
            applyRecurring: recurring > 0,
          });
          return;
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur");
        return;
      } finally {
        setBusy(false);
      }
    }
    await commitSave(body, false, false);
  }

  async function setActive(id: number, active: boolean) {
    setBusy(true);
    setError("");
    try {
      await facturxFetch<{ product: Product }>(`products.php?id=${id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: active ? 1 : 0 }),
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  async function applyConfirm() {
    if (!confirm) return;
    setBusy(true);
    setError("");
    const q = confirm.kind === "purge" ? `products.php?id=${confirm.id}&hard=1` : `products.php?id=${confirm.id}`;
    try {
      await facturxFetch(q, { method: "DELETE" });
      setConfirm(null);
      setDraft(null);
      await refresh();
      setView("list");
    } catch (e) {
      setConfirm(null);
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function askToggle(row: Product) {
    if (isActive(row)) {
      setConfirm({ kind: "off", id: row.id, label: row.label });
      return;
    }
    void setActive(row.id, true);
  }

  function askPurge(row: { id: number; label: string }) {
    setConfirm({ kind: "purge", id: row.id, label: row.label });
  }

  const viewKey = view === "form" ? `form-${draft?.id ?? "new"}` : "list";

  return (
    <div className="w-full">
      {error && view !== "form" ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      <div className="fx-page" key={viewKey}>
        {view === "list" ? (
          <>
            <PageHeader
              title="Catalogue"
              action={
                <Button
                  onClick={() => {
                    setError("");
                    setDraft(emptyDraft(defaultTaxId));
                    setView("form");
                  }}
                  disabled={busy}
                >
                  Nouveau produit
                </Button>
              }
            />
            <CatalogListBar
              q={q}
              onQ={setQ}
              showInactive={showInactive}
              onShowInactive={setShowInactive}
              count={visible.length}
            />
            <CatalogTable
              rows={visible}
              rates={rates}
              emptyText={q.trim() ? "Aucun produit ne correspond." : "Aucun produit."}
              menuId={menuId}
              onMenu={setMenuId}
              onOpen={(id) => {
                if (!busy) void openEdit(id);
              }}
              onEdit={(id) => void openEdit(id)}
              onDuplicate={(id) => void openCopy(id)}
              onToggleActive={askToggle}
              onPurge={askPurge}
              onCreateQuote={onCreateQuote}
              onCreateInvoice={onCreateInvoice}
            />
          </>
        ) : null}
        {view === "form" && draft ? (
          <CatalogForm
            draft={draft}
            setDraft={setDraft}
            rates={rates}
            decimalComma={decimalComma}
            busy={busy}
            error={error}
            onSave={() => void save()}
            onCancel={goList}
            onDeactivate={
              draft.id ? () => setConfirm({ kind: "off", id: draft.id as number, label: draft.label }) : undefined
            }
            onPurge={
              draft.id ? () => setConfirm({ kind: "purge", id: draft.id as number, label: draft.label }) : undefined
            }
          />
        ) : null}
      </div>
      {priceAsk ? (
        <div className="fx-modal" role="dialog" aria-modal="true" aria-labelledby="fx-price-title">
          <button type="button" className="fx-modal__backdrop" aria-label="Fermer" disabled={busy} onClick={() => { if (!busy) setPriceAsk(null); }} />
          <div className="fx-modal__card">
            <h2 id="fx-price-title" className="fx-modal__title">Le prix a changé</h2>
            <p className="fx-modal__text">Les pièces déjà envoyées restent telles quelles. Tu choisis ce qui suit le nouveau prix.</p>
            {priceAsk.drafts > 0 ? (
              <CheckField
                label={priceAsk.drafts > 1 ? `Mettre à jour ${priceAsk.drafts} brouillons` : "Mettre à jour 1 brouillon"}
                checked={priceAsk.applyDrafts}
                disabled={busy}
                onChange={(next) => setPriceAsk({ ...priceAsk, applyDrafts: next })}
              />
            ) : null}
            {priceAsk.recurring > 0 ? (
              <CheckField
                label={priceAsk.recurring > 1 ? `Mettre à jour ${priceAsk.recurring} récurrences` : "Mettre à jour 1 récurrence"}
                checked={priceAsk.applyRecurring}
                disabled={busy}
                onChange={(next) => setPriceAsk({ ...priceAsk, applyRecurring: next })}
              />
            ) : null}
            <p className="fx-modal__text">Décoche pour ne changer que le catalogue. La prochaine facture d’une récurrence suit son modèle.</p>
            <div className="fx-modal__actions">
              <Button variant="ghost" disabled={busy} onClick={() => setPriceAsk(null)}>Annuler</Button>
              <Button disabled={busy} onClick={() => void commitSave(priceAsk.body, priceAsk.applyDrafts, priceAsk.applyRecurring)}>
                {busy ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <ConfirmModal
        open={confirm !== null}
        title={confirm?.kind === "purge" ? "Supprimer ce produit ?" : "Désactiver ce produit ?"}
        message={
          confirm
            ? confirm.kind === "purge"
              ? `« ${confirm.label} » sera effacé du catalogue. Les pièces déjà validées gardent leur libellé.`
              : `« ${confirm.label} » ne sera plus proposé sur les nouvelles lignes. Les pièces validées ne changent pas.`
            : undefined
        }
        confirmLabel={confirm?.kind === "purge" ? "Supprimer" : "Désactiver"}
        busyLabel={confirm?.kind === "purge" ? "Suppression…" : "Désactivation…"}
        busy={busy}
        onCancel={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => void applyConfirm()}
      />
    </div>
  );
}
